<template>
  <div class="attend-page">
    <van-nav-bar title="考勤记录" fixed placeholder>
      <template #right>
        <span class="nav-action" @click="goLeaveList">
          <van-icon name="edit" size="16" color="#52C1B2" />
          <span style="color: #52c1b2; font-size: 13px">请假管理</span>
        </span>
      </template>
    </van-nav-bar>

    <!-- 月份选择器 -->
    <div class="month-selector">
      <van-icon name="arrow-left" @click="prevMonth" />
      <span class="current-month">{{ currentMonth }}</span>
      <van-icon name="arrow" @click="nextMonth" />
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <div class="stat-card present">
        <div class="stat-number">{{ presentCount }}</div>
        <div class="stat-label">出勤</div>
      </div>
      <div class="stat-card absent">
        <div class="stat-number">{{ absentCount }}</div>
        <div class="stat-label">缺勤</div>
      </div>
    </div>

    <!-- 日历 -->
    <div class="calendar-wrapper">
      <div class="week-header">
        <span v-for="day in weekDays" :key="day">{{ day }}</span>
      </div>
      <div class="calendar-grid">
        <div
          v-for="(day, index) in calendarDays"
          :key="index"
          class="calendar-day"
          :class="{
            'other-month': !day.isCurrentMonth,
            today: day.isToday,
            selected: day.date === selectedDate,
            'has-record': day.hasRecord,
            present: day.status === 1,
          }"
          @click="selectDate(day)"
        >
          <span class="day-number">{{ day.day }}</span>
          <span
            class="status-dot"
            v-if="day.hasRecord"
            :class="day.status === 1 ? 'present' : 'absent'"
          ></span>
        </div>
      </div>
    </div>

    <!-- 选中日期详情 -->
    <div class="day-detail" v-if="selectedDate">
      <div class="detail-header">
        <span>{{ selectedDate }} 考勤详情</span>
      </div>
      <div class="detail-list" v-if="dayAttendances.length > 0">
        <div class="detail-item" v-for="(item, index) in dayAttendances" :key="index">
          <div class="detail-row">
            <span class="detail-label">考勤状态：</span>
            <span class="detail-value" :class="item.attendance_status == 1 ? 'status-present' : 'status-absent'">
              {{ item.attendance_status == 1 ? '出勤' : '缺勤/取消出勤' }}
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">班级名称：</span>
            <span class="detail-value">{{ item.class_name || '未设置' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">服务时间：</span>
            <span class="detail-value">{{ item.service_time || '未设置' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">记录时间：</span>
            <span class="detail-value">{{ item.create_at || '未记录' }}</span>
          </div>
        </div>
      </div>
      <van-empty v-else description="暂无考勤详情数据" />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
//import { Toast } from "vant";
import api from "../service/api";

export default {
  setup() {
    const router = useRouter();

    const currentYear = ref("");
    const currentMonth = ref("");
    const currentMonthValue = ref("");
    const selectedDate = ref("");
    const monthData = ref({});
    const dayAttendances = ref([]);

    const weekDays = ["一", "二", "三", "四", "五", "六", "日"];

    const now = new Date();
    const initMonth = () => {
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      currentYear.value = y;
      currentMonthValue.value = `${y}-${m}`;
      currentMonth.value = `${y}年${parseInt(m)}月`;
      selectedDate.value = `${y}-${m}-${d}`;
    };
    initMonth();

    // 统计
    const presentCount = computed(() => {
      return Object.values(monthData.value).filter((v) => v === 1).length;
    });

    const absentCount = computed(() => {
      return Object.values(monthData.value).filter((v) => v === 2).length;
    });

    // 日历天数
    const calendarDays = computed(() => {
      const parts = currentMonthValue.value.split("-");
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;

      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const days = [];

      const firstDayWeek = firstDay.getDay() || 7;
      for (let i = firstDayWeek - 1; i > 0; i--) {
        const d = new Date(firstDay);
        d.setDate(d.getDate() - i);
        const dateStr = formatDate(d);
        days.push({
          date: dateStr,
          day: d.getDate(),
          isCurrentMonth: false,
          isToday: isSameDay(d, now),
          hasRecord: monthData.value[dateStr] === 1 || monthData.value[dateStr] === 2,
          status: monthData.value[dateStr],
        });
      }

      for (let i = 1; i <= lastDay.getDate(); i++) {
        const d = new Date(year, month, i);
        const dateStr = formatDate(d);
        days.push({
          date: dateStr,
          day: i,
          isCurrentMonth: true,
          isToday: isSameDay(d, now),
          hasRecord: monthData.value[dateStr] === 1 || monthData.value[dateStr] === 2,
          status: monthData.value[dateStr],
        });
      }

      const lastDayWeek = lastDay.getDay() || 7;
      for (let i = 1; i <= 7 - lastDayWeek; i++) {
        const d = new Date(lastDay);
        d.setDate(d.getDate() + i);
        const dateStr = formatDate(d);
        days.push({
          date: dateStr,
          day: d.getDate(),
          isCurrentMonth: false,
          isToday: isSameDay(d, now),
          hasRecord: monthData.value[dateStr] === 1 || monthData.value[dateStr] === 2,
          status: monthData.value[dateStr],
        });
      }
      return days;
    });

    function formatDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }

    function isSameDay(d1, d2) {
      return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
      );
    }

    // 获取月考勤数据
    const loadMonthData = async () => {
      try {
        const childId = sessionStorage.getItem("childId");
        if (!childId) return;
        const res = await api.getStudentAttendance({
          student_id: childId,
          year: currentMonthValue.value.split("-")[0],
          month: currentMonthValue.value.split("-")[1],
        });
        if (res.data.code == 1001) {
          const result = res.data.result;
          if (typeof result === "string") {
            monthData.value = JSON.parse(result);
          } else {
            monthData.value = result || {};
          }
        }
      } catch (error) {
        console.error("获取考勤数据失败:", error);
        monthData.value = {};
      }
    };

    // 获取某天考勤详情
    const loadDayAttendance = async () => {
      try {
        const childId = sessionStorage.getItem("childId");
        if (!childId) return;
        const res = await api.getStudentAttendanceByDate({
          student_id: childId,
          search_date: selectedDate.value,
        });
        if (res.data.code == 1001) {
          dayAttendances.value = res.data.result || [];
        }
      } catch (error) {
        dayAttendances.value = [];
      }
    };

    const selectDate = (day) => {
      selectedDate.value = day.date;
      if (!day.isCurrentMonth) {
        currentMonthValue.value = day.date.substring(0, 7);
        const parts = currentMonthValue.value.split("-");
        currentMonth.value = `${parts[0]}年${parseInt(parts[1])}月`;
        loadMonthData();
      }
      loadDayAttendance();
    };

    const prevMonth = () => {
      const d = new Date(currentMonthValue.value + "-01");
      d.setMonth(d.getMonth() - 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      console.log(m + "");
      currentMonthValue.value = `${y}-${m}`;
      currentMonth.value = `${y}年${parseInt(m)}月`;
      loadMonthData();
    };

    const nextMonth = () => {
      const d = new Date(currentMonthValue.value + "-01");
      d.setMonth(d.getMonth() + 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      currentMonthValue.value = `${y}-${m}`;
      currentMonth.value = `${y}年${parseInt(m)}月`;
      loadMonthData();
    };

    onMounted(() => {
      loadMonthData();
      loadDayAttendance();
    });

    // 跳转家长请假记录页（请假管理入口，与小程序 attend 的"请假管理"一致）
    const goLeaveList = () => {
      router.push("/leaveList");
    };

    return {
      currentMonth,
      selectedDate,
      weekDays,
      calendarDays,
      presentCount,
      absentCount,
      dayAttendances,
      selectDate,
      prevMonth,
      nextMonth,
      goLeaveList,
    };
  },
};
</script>

<style lang="less" scoped>
.attend-page {
  min-height: 100vh;
  background: #f2f2f2;
  padding-bottom: 70px;
}

.month-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 15px;
  background: #fff;
}

.current-month {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  min-width: 100px;
  text-align: center;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  padding: 10px 15px;
  background: #fff;
}

.stat-card {
  border-radius: 10px;
  padding: 15px;
  text-align: center;
}

.stat-card.present {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
}

.stat-card.absent {
  background: linear-gradient(135deg, #fff2f0 0%, #ffccc7 100%);
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

.calendar-wrapper {
  background: #fff;
  margin: 10px;
  border-radius: 10px;
  padding: 15px;
}

.week-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 8px;

  span {
    font-size: 13px;
    color: #666;
  }
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.calendar-day {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;

  &.other-month .day-number {
    color: #ccc;
  }

  &.today {
    background: var(--color-primary-light);
    .day-number {
      color: var(--color-primary);
      font-weight: 600;
    }
  }

  &.selected {
    background: var(--color-primary);
    .day-number {
      color: #fff;
    }
    .status-dot.present {
      background: #fff;
    }
  }
}

.day-number {
  font-size: 13px;
  color: #333;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-top: 2px;

  &.present {
    background: #52c41a;
  }
  &.absent {
    background: #ff4d4f;
  }
}

.day-detail {
  margin: 10px;
  background: #fff;
  border-radius: 10px;
  padding: 15px;
}

.detail-header {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.detail-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-item {
  padding: 12px;
  background: #f9f9f9;
  border-radius: 8px;
  border-left: 3px solid #e0e0e0;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-row {
  display: flex;
  align-items: baseline;
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 1.6;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-label {
  color: #666;
  width: 85px;
  flex-shrink: 0;
  font-weight: 500;
}

.detail-value {
  color: #333;
  flex: 1;
}

.status-present {
  color: #07c160;
  font-weight: 600;
}

.status-absent {
  color: #fa5151;
  font-weight: 600;
}
</style>
